import { atom, getDefaultStore, useAtomValue, useSetAtom } from "jotai"
import { atomFamily } from "jotai-family"
import { Node } from "reactflow"

export const isModifiedAtom = atom(false)

const nodeSelectionIdsAtom = atom<Set<Node["id"]>>(new Set<Node["id"]>())

const nodeSelectionFamily = atomFamily((_id: Node["id"]) => atom<Node | null>(null))

export const nodeSelectionAtom = atom((get) => {
  const ids = get(nodeSelectionIdsAtom)
  const result: Record<Node["id"], Node> = {}
  for (const id of ids) {
    const node = get(nodeSelectionFamily(id))
    if (node) result[id] = node
  }
  return result
})

if (process.env.NODE_ENV !== "production") {
  isModifiedAtom.debugLabel = "isModifiedAtom"
  nodeSelectionIdsAtom.debugLabel = "nodeSelectionIdsAtom"
  nodeSelectionAtom.debugLabel = "nodeSelectionAtom"
}

export const useNodeSelection = () => useAtomValue(nodeSelectionAtom)

export const useSetNodeSelection = () => {
  const setIds = useSetAtom(nodeSelectionIdsAtom)

  const addNode = (node: Node) => {
    getDefaultStore().set(nodeSelectionFamily(node.id), node)
    setIds((prev: Set<Node["id"]>) => new Set([...prev, node.id]))
  }

  const removeNode = (id: Node["id"]) => {
    nodeSelectionFamily.remove(id)
    setIds((prev: Set<Node["id"]>) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const clearNodes = () => {
    const ids = getDefaultStore().get(nodeSelectionIdsAtom)
    for (const id of ids) {
      nodeSelectionFamily.remove(id)
    }
    setIds(new Set<Node["id"]>())
  }

  return { addNode, removeNode, clearNodes }
}
